import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from './Icon';
import { Badge } from './Badge';

export interface SwitcherOption {
  value: string;
  label: string;
  sublabel?: string;
  badge?: string;
}

interface SwitcherProps {
  icon?: IconName;
  label: string;
  value: string;
  options: SwitcherOption[];
  onChange: (value: string) => void;
  width?: number;
  compact?: boolean;
}

export function Switcher({
  icon,
  label,
  value,
  options,
  onChange,
  width = 200,
  compact,
}: SwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const current = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          padding: '0 10px',
          background: '#fff',
          border: '1px solid var(--bx-border)',
          borderRadius: 'var(--bx-radius-md)',
          cursor: 'pointer',
          fontFamily: 'var(--bx-font-sans)',
          fontSize: 12.5,
          color: 'var(--bx-text-dark)',
          minWidth: width,
          maxWidth: width + 60,
        }}
      >
        {icon && (
          <Icon
            name={icon}
            size={13}
            style={{ color: 'var(--bx-text-muted)', flexShrink: 0 }}
          />
        )}
        <div
          style={{
            flex: 1,
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.15,
            minWidth: 0,
          }}
        >
          {!compact && (
            <span
              style={{
                fontSize: 10,
                color: 'var(--bx-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
          )}
          <span
            style={{
              fontWeight: 600,
              marginTop: compact ? 0 : 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {current ? current.label : '—'}
          </span>
        </div>
        <Icon
          name="chevron-down"
          size={11}
          style={{ color: 'var(--bx-text-muted)', flexShrink: 0 }}
        />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: width + 40,
            maxWidth: 320,
            background: '#fff',
            border: '1px solid var(--bx-border-muted)',
            borderRadius: 'var(--bx-radius-md)',
            boxShadow: 'var(--bx-shadow-pop)',
            zIndex: 60,
            padding: 4,
          }}
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  border: 'none',
                  background: active ? '#eef2ff' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: 6,
                  color: 'var(--bx-text-dark)',
                  fontFamily: 'var(--bx-font-sans)',
                  fontSize: 12.5,
                  textAlign: 'left',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: active ? 600 : 500, whiteSpace: 'nowrap' }}>
                    {o.label}
                  </span>
                  {o.sublabel && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        color: 'var(--bx-text-muted)',
                      }}
                    >
                      {o.sublabel}
                    </span>
                  )}
                </span>
                {o.badge && <Badge kind={o.badge} size="sm">{o.badge}</Badge>}
                {active && (
                  <Icon name="check" size={11} style={{ color: 'var(--bx-primary)' }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
